import { useEffect, useState } from "react";
import { Tab } from "@headlessui/react";
import cls from "classnames";
import { useRouter } from "next/router";

import ChannelsTab from "./ChannelsTab";
import TestimonialsTab from "./TestimonialsTab";

import { debounce } from "@qoohoo/common/utils/functions";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { button, pageview } from "@qoohoo/common/constants/analytics";

export default function TabSection({ userData, isProfileOwner }) {
  const router = useRouter();
  const isCreator = userData.is_creator;
  const subscriptionType = ["recurring", "free_subscription"];

  const filteredChannels = userData?.channels.reduce(
    (acc, channel) => {
      const isSubscription = subscriptionType.includes(channel.type);
      if (isSubscription) {
        return {
          ...acc,
          Subscriptions: [...acc.Subscriptions, channel],
        };
      } else {
        return {
          ...acc,
          "Sessions & Courses": [...acc["Sessions & Courses"], channel],
        };
      }
    },
    {
      "Sessions & Courses": [],
      Subscriptions: [],
    }
  );

  const INITIAL_STATE = userData.is_creator
    ? {
        "Sessions & Courses": filteredChannels["Sessions & Courses"],
        Subscriptions: filteredChannels["Subscriptions"],
        Testimonials: userData.testimonials,
      }
    : {
        Attending: filteredChannels["Sessions & Courses"],
        "Subscribed to": filteredChannels["Subscriptions"],
      };

  const [categories, setCategories] = useState(INITIAL_STATE);

  function getInitialTab() {
    if (categories["Sessions & Courses"]?.length > 0) {
      return 0;
    } else if (categories["Subscriptions"]?.length > 0) {
      return 1;
    } else {
      return 0;
    }
  }

  function returnActiveTab(activeTab) {
    switch (activeTab) {
      case 0:
        return "sessions-and-courses";
      case 1:
        return "subscriptions";
      case 2:
        return "testimonials";
      case "sessions-and-events":
        return 0;
      case "subscriptions":
        return 1;
      case "testimonials":
        return 2;
      default:
        return 0;
    }
  }

  const [windowWidth, setWindowWidth] = useState(0);
  const [activeTab, setActiveTab] = useState(
    returnActiveTab(router.asPath.split("#")[1]) || getInitialTab()
  );

  const moveTabSelectionBar = (targetTab, tabList, selectionBar) => {
    const rectTargetTab = targetTab?.getBoundingClientRect();
    const rectTabList = tabList?.getBoundingClientRect();
    const selectionBarWidth = 1;
    const selectionBarPosition =
      rectTargetTab?.width / 2 + (rectTargetTab?.left - rectTabList?.left) - 12;

    selectionBar.style.transform = `matrix(${selectionBarWidth}, 0, 0, 1, ${selectionBarPosition}, 1)`;
  };

  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowWidth(window.innerWidth);
    }, 500);

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const tabList = document.querySelector(".tablist");
    const tabs = document.querySelectorAll(".tablist__tab");
    const activeTab = document.querySelector(".tablist__tab.is-active");
    const selectionBar = document.querySelector(".selection-bar");

    Array.from(tabs).forEach((tab) => {
      tab.addEventListener("click", (event) => {
        moveTabSelectionBar(event.target, tabList, selectionBar);
      });
    });

    moveTabSelectionBar(activeTab, tabList, selectionBar);
  }, [windowWidth, activeTab]);

  useEffect(() => {
    router.push(`#${returnActiveTab(activeTab)}`, undefined, {
      shallow: true,
    });
  }, [activeTab]);

  useEffect(() => {
    setCategories(INITIAL_STATE);
  }, [userData?.channels?.length]);

  return (
    <section
      className={cls(
        "mt-12 lg:mt-[120px] mx-auto max-w-[630px]",
        !userData.is_creator && "mb-14 lg:mb-[72px]"
      )}
    >
      <div className="w-full">
        <Tab.Group
          defaultIndex={activeTab}
          onChange={(index) => {
            Track.buttonClick(button.profile_tab, {
              actionType: "click",
              label: returnActiveTab(index),
              current_screen: pageview.profile,
            });
            setActiveTab(index);
          }}
        >
          <Tab.List className="relative sm:max-w-[494px] mx-auto flex justify-evenly bg-[#1F2429] sm:bg-transparent tablist">
            {Object.keys(categories).map((category) => (
              <Tab
                key={category}
                className={({ selected }) =>
                  cls(
                    "w-auto text-sm lg:text-xl leading-5 lg:leading-6 rounded-full lg:px-2 focus:outline-none lg:even:mx-4 tablist__tab",
                    selected
                      ? "tab-active-border pt-4 is-active"
                      : "tab-deactive-border text-[#A6A6A6] lg:hover:text-white pt-4"
                  )
                }
                onClick={() => {
                  Track.buttonClick(button.profile_tab, {
                    actionType: "click",
                    label: category,
                    current_screen: pageview.profile,
                  });
                }}
              >
                {category}
              </Tab>
            ))}
            <div className="selection-bar"></div>
          </Tab.List>
          <Tab.Panels className="text-center">
            {Object.values(categories)
              .slice(0, 2)
              .map((posts, idx) => (
                <ChannelsTab
                  key={idx}
                  posts={posts}
                  title={Object.keys(categories)[idx].toLowerCase()}
                  isProfileOwner={isProfileOwner}
                  isCreator={isCreator}
                />
              ))}
            <TestimonialsTab
              key="Testimonials"
              testimonials={categories.Testimonials}
            />
          </Tab.Panels>
        </Tab.Group>
      </div>
    </section>
  );
}
